import { useCallback, useEffect, useState } from 'react';
import { api } from './api.ts';

const KEY = 'newflix:favs';

function readInitial(): string[] {
  if (typeof window === 'undefined') return []; // SSR/prerender: no localStorage
  try {
    const raw = localStorage.getItem(KEY);
    if (raw) return JSON.parse(raw) as string[];
  } catch {
    // ignore
  }
  return [];
}

export function useFavorites(): [string[], (slug: string) => void] {
  // Start empty so SSR/prerender HTML matches the first client render (hydrate).
  // localStorage is applied after mount.
  const [favs, setFavs] = useState<string[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setFavs(readInitial());
    setReady(true);
  }, []);

  useEffect(() => {
    if (!ready) return;
    localStorage.setItem(KEY, JSON.stringify(favs));
  }, [favs, ready]);

  const toggle = useCallback((slug: string) => {
    let willAdd = true;
    setFavs((f) => {
      const has = f.includes(slug);
      willAdd = !has;
      return has ? f.filter((x) => x !== slug) : [...f, slug];
    });
    const sync = willAdd ? api.upvote(slug) : api.removeUpvote(slug);
    sync.catch((err: Error & { status?: number }) => {
      // 401 = not logged in: keep local-only fav, no rollback needed
      if (err.status === 401) return;
      // Other errors: rollback local state
      setFavs((f) => (willAdd ? f.filter((x) => x !== slug) : [...f, slug]));
      console.error('[favorites] sync failed', err);
    });
  }, []);

  return [favs, toggle];
}
